import { useState } from 'react';
import { Card } from './ui/card';
import { Heart, Zap, Cloud, AlertCircle, Smile } from 'lucide-react';

interface EmotionalState {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  value: number;
}

const EMOTIONAL_STATES: EmotionalState[] = [
  {
    id: 'anxious',
    label: 'Anxious',
    description: 'Worried, uncertain, or on edge',
    icon: <AlertCircle className="w-6 h-6" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    value: 1,
  },
  {
    id: 'uncertain',
    label: 'Uncertain',
    description: 'Confused, unclear, or hesitant',
    icon: <Cloud className="w-6 h-6" />,
    color: 'text-neutral-600',
    bgColor: 'bg-neutral-100',
    borderColor: 'border-neutral-300',
    value: 2,
  },
  {
    id: 'neutral',
    label: 'Neutral',
    description: 'Balanced, steady, or calm',
    icon: <Smile className="w-6 h-6" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    value: 3,
  },
  {
    id: 'hopeful',
    label: 'Hopeful',
    description: 'Optimistic, positive, or encouraged',
    icon: <Zap className="w-6 h-6" />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    value: 4,
  },
  {
    id: 'connected',
    label: 'Connected',
    description: 'Close, secure, or deeply bonded',
    icon: <Heart className="w-6 h-6" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-300',
    value: 5,
  },
];

interface Props {
  selectedStates: number[];
  onStateSelect: (value: number) => void;
  maxSelections: number;
  title?: string;
  subtitle?: string;
}

export function EmotionalStateGauge({
  selectedStates,
  onStateSelect,
  maxSelections,
  title = 'How do you feel right now?',
  subtitle = 'Select your emotional state 5 times to capture your genuine feelings',
}: Props) {
  const [hoveredState, setHoveredState] = useState<string | null>(null);

  const getSelectionCount = (value: number) => {
    return selectedStates.filter((s) => s === value).length;
  };

  const canSelect = selectedStates.length < maxSelections;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-light text-neutral-900">{title}</h2>
        <p className="text-sm text-neutral-600">{subtitle}</p>
        <div className="flex items-center justify-center gap-2 mt-4">
          {[...Array(maxSelections)].map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-all ${
                i < selectedStates.length
                  ? 'bg-violet-600 scale-110'
                  : 'bg-neutral-200'
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          {selectedStates.length}/{maxSelections} selections
        </p>
      </div>

      <div className="grid gap-3">
        {EMOTIONAL_STATES.map((state) => {
          const count = getSelectionCount(state.value);
          const isHovered = hoveredState === state.id;
          const isDisabled = !canSelect;

          return (
            <button
              key={state.id}
              onClick={() => canSelect && onStateSelect(state.value)}
              onMouseEnter={() => setHoveredState(state.id)}
              onMouseLeave={() => setHoveredState(null)}
              disabled={isDisabled}
              className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                count > 0
                  ? `${state.bgColor} ${state.borderColor} shadow-sm`
                  : isHovered && !isDisabled
                  ? `bg-neutral-50 border-neutral-300 scale-[1.02]`
                  : 'bg-white border-neutral-200 hover:border-neutral-300'
              } ${isDisabled && count === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-start gap-4">
                <div className={`${state.color} ${count > 0 ? 'scale-110' : ''} transition-transform`}>
                  {state.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium text-neutral-900">{state.label}</h3>
                    {count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${state.bgColor} ${state.color}`}>
                        ×{count}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600">{state.description}</p>
                </div>
              </div>

              {/* Visual feedback for multiple selections */}
              {count > 0 && (
                <div className="absolute bottom-2 right-2 flex gap-1">
                  {[...Array(count)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${state.color.replace('text-', 'bg-')}`}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Emotional Summary */}
      {selectedStates.length > 0 && (
        <Card className="p-4 bg-neutral-50 border-neutral-200">
          <h4 className="text-sm font-medium text-neutral-900 mb-2">Your Emotional Pattern</h4>
          <div className="flex flex-wrap gap-2">
            {EMOTIONAL_STATES.map((state) => {
              const count = getSelectionCount(state.value);
              if (count === 0) return null;

              return (
                <div
                  key={state.id}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${state.bgColor} ${state.color}`}
                >
                  {state.label} ({count})
                </div>
              );
            })}
          </div>
          <p className="text-xs text-neutral-500 mt-3">
            {selectedStates.length < maxSelections
              ? `Select ${maxSelections - selectedStates.length} more to complete your emotional snapshot`
              : 'Emotional snapshot complete ✓'}
          </p>
        </Card>
      )}
    </div>
  );
}
